<template>
  <b-modal
    id="team-players-modal"
    :ok-title="trans('close')"
    ok-only
    size="lg"
    @shown="onShown"
    @hidden="onHidden"
  >
    <template #modal-title>
      {{ trans('players') }}
      <span v-if="!!team">- {{ team.name }}</span>
      <small v-if="!!team" class="text-muted ml-2">
        # {{ team.id }}
      </small>
    </template>

    <b-row class="mb-3">
      <b-col class="pb-2">
        <small class="text-muted d-block">{{ trans('manager') }}</small>
        <address v-if="!!managerHtml" v-html="managerHtml" class="mb-1"></address>
        <em v-if="!managerHtml" class="text-muted d-block mb-1">{{ trans('none') }}</em>
        <b-button v-if="!!managerHtml" variant="outline-primary" size="sm" class="py-0" @click="onClickMessageManager">
          <i class="fa fa-fw fa-comments"></i>
          {{ trans('message') }}
        </b-button>
        <b-button v-if="players.length > 0" variant="outline-primary" size="sm" class="py-0 ml-2" v-b-modal.change-team-manager-modal :disabled="busy">
          <i class="fa fa-fw fa-edit"></i>
          {{ trans('change') }}
        </b-button>
      </b-col>
      <b-col>
        <small class="text-muted d-block">{{ trans('team_invoice') }}</small>
        <div v-if="!!teamInvoiceUrl" class="mb-2">
          <b-link v-if="!hideInvoiceLinks" target="invoice" :href="teamInvoiceUrl">
            # {{ team.invoice_no }}
            <small><i class="fa fa-fw fa-external-link-alt"></i></small>
          </b-link>
          <div v-if="teamTotal > 0">
            {{ trans('total')}}:
            <dollar :value="teamTotal"></dollar>
          </div>
          <div v-if="teamBalance > 0" class="text-danger">
            {{ trans('balance')}}:
            <dollar :value="teamBalance"></dollar>
          </div>
        </div>
        <em v-if="!teamInvoiceUrl" class="d-block mb-2">
          {{ trans('none') }} ({{ trans('house_team').toLowerCase() }})
        </em>
        <b-button v-if="players.length > 0" variant="outline-primary" size="sm" class="py-0" @click="onClickMessageTeam">
          <i class="fa fa-fw fa-comments"></i>
          {{ trans('message') }}
        </b-button>
      </b-col>
    </b-row>

    <strong class="d-block">{{ trans('players') }}</strong>
    <b-table
      class="bg-white"
      bordered
      small
      hover
      responsive="sm"
      show-empty
      :fields="fields"
      :items="players"
    >
      <template #empty>
        <h4 v-if="busy" align="center">
          <i class="fa fa-circle-notch fa-spin"></i>
          {{ trans('loading') }}
        </h4>
        <p v-if="!busy && !error" align="center">{{ trans('none') }}</p>
        <p v-if="error" class="text-danger" align="center">
          {{ error }}
        </p>
      </template>
      <template #cell(invoice_no)="data">
        <b-link v-if="!!data.value" size="sm" target="invoice" :href="`/organization/${organization.id}/invoice?invoice_no=${data.value}`">
          # {{ data.value }}
          <small><i class="fa fa-fw fa-external-link-alt"></i></small>
        </b-link>
      </template>
      <template #cell(name)="data">
        <i class="fa fa-fw fa-user"></i>
        {{ data.value }}
        <small v-if="!!data.item.date_of_birth" class="d-block text-muted">{{ data.item.age }} ({{ data.item.date_of_birth }})</small>
        <small v-if="!!data.item.email" class="d-block text-muted">{{ data.item.email }}</small>
        <small v-if="!!data.item.mobile" class="d-block text-muted">{{ data.item.mobile }}</small>
      </template>
      <template #cell(invoice_total)="data">
        <span v-if="data.value !== null">
          {{ numeral(data.value).format('1,000.00') }}
        </span>
      </template>
      <template #cell(invoice_balance)="data">
        <span v-if="data.value !== null" :class="data.value > 0 ? 'text-danger' : ''">
          {{ numeral(data.value).format('1,000.00') }}
        </span>
      </template>
    </b-table>

    <change-team-manager-modal
      :organization="organization"
      :team-and-roster="team_and_roster"
      @changed="onChanged"
    ></change-team-manager-modal>
  </b-modal>
</template>

<script>
  import Dollar from './../billing/Dollar'
  import ChangeTeamManagerModal from './ChangeTeamManagerModal'

  export default {
    props: {
      organization: Object,
      team: Object,
      hideInvoiceLinks: {
        type: Boolean,
        default: false,
      },
    },
    components: {
      Dollar,
      ChangeTeamManagerModal,
    },
    data() {
      return {
        trans: trans,
        numeral: numeral,
        busy: false,
        error: null,
        team_and_roster: null,
        fields: _.compact([
          {
            key: 'name',
            label: trans('name'),
            sortable: true,
          },
          {
            key: 'game_jersey',
            label: trans('jersey'),
            sortable: true,
          },
          {
            key: 'invoice_total',
            label: trans('total') + ' $',
            sortable: true,
          },
          {
            key: 'invoice_balance',
            label: trans('balance') + ' $',
            sortable: true,
          },
          this.hideInvoiceLinks ? null : {
            key: 'invoice_no',
            label: trans('invoice'),
            sortable: true,
          },
        ]),
      }
    },
    computed: {
      managerHtml() {
        if (!this.team_and_roster || !this.team_and_roster.manager) return null
        return _.compact([
          `<strong>${this.team_and_roster.manager.name}</strong>`,
          this.team_and_roster.manager.email,
          this.team_and_roster.manager.mobile
        ]).join('<br/>')
      },
      teamInvoiceUrl() {
        if (!this.team || !this.team.invoice_no) return null
        return `/organization/${this.organization.id}/invoice?invoice_no=${this.team.invoice_no}`
      },
      teamTotal() {
        if (!this.team || !this.team.invoice_total) return null
        return this.team.invoice_total
      },
      teamBalance() {
        if (!this.team || !this.team.invoice_balance) return null
        return this.team.invoice_balance
      },
      players() {
        return !!this.team_and_roster ? this.team_and_roster.players : []
      },
    },
    methods: {
      onClickMessageTeam() {
        this.$emit('message-team')
        this.$bvModal.hide('team-players-modal')
      },
      onClickMessageManager() {
        this.$emit('message-manager')
        this.$bvModal.hide('team-players-modal')
      },
      onShown() {
        window.gamesheetTeamModal = this
        this.fetchTeamAndRoster()
      },
      onHidden() {
        this.busy = false
        this.error = null
        this.team_and_roster = null
      },
      onChanged(team_and_roster) {
        this.team_and_roster = team_and_roster
        this.$emit('changed')
      },
      fetchTeamAndRoster() {
        this.busy = true
        this.error = null
        let url = `/organization/${this.organization.id}/team/${this.team.id}`
        axios.get(url)
        .then(response => {
          this.busy = false
          this.team_and_roster = response.data
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
        })
      },
    },
    mounted() {
      window.teamPlayersModal = this
    },
  }
</script>
